
import { BillingType, UserType, Plan } from '../types/plan';
import { collection, getDocs, query } from 'firebase/firestore';


/**
 * Classe utilitária para lógica de planos (Single Responsibility)
 */
export class PlanService {
  // Formatar período do plano
  static formatPeriod(quantity: number, billingType: BillingType, isRecurring: boolean): string {
    const sessionLabel = quantity === 1 ? 'sessão' : 'sessões';
    const hourLabel = quantity === 1 ? 'hora' : 'horas';
    const label = billingType === BillingType.SESSIONS ? sessionLabel : hourLabel;
    
    if (isRecurring) {
      return `${quantity} ${label} por mês`;
    }
    return `${quantity} ${label}`;
  }

  // Obter texto do botão baseado no tipo de plano
  static getButtonText(isRecurring: boolean): string {
    return isRecurring ? 'Assinar Plano' : 'Comprar Agora';
  }

  // Formatar valor como moeda brasileira
  static formatCurrency(value: number): string {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  // Obter label do tipo de cobrança
  static getBillingTypeLabel(billingType: BillingType, plural: boolean = true): string {
    if (billingType === BillingType.SESSIONS) {
      return plural ? 'Sessões' : 'Sessão';
    }
    return plural ? 'Horas' : 'Hora';
  }

  // Obter label do tipo de usuário
  static getUserTypeLabel(userType: UserType): string {
    return userType === UserType.PATIENT ? 'Paciente' : 'Terapeuta';
  }

  // Mapear dados do Firestore para Plan
  static fromFirestore(id: string, data: any): Plan {
    return {
      id,
      name: data.name || '',
      value: data.value || 0,
      billingType: data.billingType as BillingType,
      quantity: data.quantity || 0,
      userType: data.userType as UserType,
      isRecurring: data.isRecurring || false,
      description: data.description || '',
      isFeatured: data.isFeatured || false,
      sessionLabel: data.sessionLabel || '',
      features: data.features || [],
      deleted: data.deleted || false,
      therapistCommission: data.therapistCommission ?? 0,
      platformFee: data.platformFee ?? 100,
    };
  }

  // Preparar dados para salvar no Firestore
  static toFirestore(plan: Omit<Plan, 'id'>): any {
    return {
      name: plan.name,
      value: plan.value,
      billingType: plan.billingType,
      quantity: plan.quantity,
      userType: plan.userType,
      isRecurring: plan.isRecurring,
      description: plan.description,
      isFeatured: plan.isFeatured || false,
      sessionLabel: plan.sessionLabel || '',
      features: plan.features || [],
      deleted: plan.deleted || false,
      therapistCommission: plan.therapistCommission ?? 0,
      platformFee: plan.platformFee ?? 100,
    };
  }

  // Buscar planos não deletados do Firestore
  static async fetchActivePlans(db: any): Promise<Plan[]> {
    // Importações estáticas para garantir compatibilidade
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    // @ts-ignore
    // eslint-disable-next-line import/no-extraneous-dependencies
    // Importar do firebase/firestore
    // (já está importado no topo do projeto, mas garantir aqui)
    // import { collection, getDocs, query } from 'firebase/firestore';
    const plansQuery = query(collection(db, 'plans'));
    const snapshot = await getDocs(plansQuery);
    let plansList: Plan[] = snapshot.docs.map((doc: any) => PlanService.fromFirestore(doc.id, doc.data()));
    plansList = plansList.filter((plan: Plan) => !plan.deleted);
    plansList.sort((a: Plan, b: Plan) => a.name.localeCompare(b.name));
    return plansList;
  }
}
